/**
 * "Show where" in a drawer header (twin slice 1, plan W4 step 14).
 *
 * A LINK, never a drawer-opener: it goes to the Overview pointed at this
 * robot, lift, door or task (`/overview?site=…&view=…&focus=…&from=drawer`),
 * and what opens there is the person's own next tap. It never carries a
 * camera pose. A robot is always fleet-qualified (`robot:<fleet>/<name>`):
 * a robot with no fleet gets no link, because two fleets may hold the same
 * name and a link must not pick for the reader.
 *
 * Its words name its view, as the catalogue's do (§8): "Show in building"
 * (`view=3d`) -- deep links open Building, Decision 7 -- unless something
 * this page knows rules the Building view out here (the flag, the role, no
 * building model for this site, a screen under 768 px), when it is "Show
 * on map" (`view=2d`). The rules are `twinAvailability`'s, the Overview's
 * own; a device that turns out unable to draw is the Overview's to say.
 *
 * A real `<a href>`, so a long-press, a middle click or "open in new tab"
 * works (a new tab is an initial load with locate parameters, which is a
 * landing). A plain click is a PUSH with a fresh landing id
 * (`show-where.ts`), minted per press rather than per render, so pressing
 * it twice is two landings.
 *
 * Renders nothing without an active site (no link can be built), and
 * degrades to a plain anchor outside a router, where the drawers' own
 * component tests render it.
 */
import { useContext, type MouseEvent } from 'react';
import { useHref, useInRouterContext, useNavigate } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import { getOptionalRuntimeConfig } from '../config/runtime-config';
import { twinAssetFor } from '../screens/Twin/twin-assets';
import { SiteContext } from '../state/SiteContext';
import type { LocateFocus } from '../state/locate';
import { drawerShowWhere, newLandingState, showWhereHref } from '../state/show-where';
import { twinAvailability } from '../state/twin-availability';
import './ShowWhereLink.css';

export function ShowWhereLink({ focus }: { focus: LocateFocus | null }) {
  const site = useContext(SiteContext)?.site?.id ?? null;
  const roles = useContext(AuthContext)?.roles ?? null;
  const inRouter = useInRouterContext();
  const { view, label } = drawerShowWhere(
    twinAvailability({
      twinEnabled: getOptionalRuntimeConfig()?.featureFlags.twinEnabled,
      roles,
      hasAsset: twinAssetFor(site) !== null,
      screenWidthPx: typeof window === 'undefined' ? null : window.innerWidth,
      // Only a canvas that has run can report one.
      graphics: null,
    }),
  );
  // A robot focus is fleet-qualified by its type; one built from an empty
  // fleet would serialise as `robot:/name`, which the link reader refuses.
  const usable = focus && !(focus.kind === 'robot' && (!focus.fleetName || !focus.robotName));
  const href = usable ? showWhereHref(site, view, focus, { kind: 'drawer' }) : null;
  if (!href) return null;
  return inRouter ? (
    <RouterShowWhereLink to={href} label={label} />
  ) : (
    <a className="nx-show-where-link" href={href} data-testid="show-where-link">
      {label}
    </a>
  );
}

function RouterShowWhereLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  const href = useHref(to);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Leave every new-tab and new-window gesture to the browser.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    navigate(to, { state: newLandingState() });
  };
  return (
    <a className="nx-show-where-link" href={href} onClick={onClick} data-testid="show-where-link">
      {label}
    </a>
  );
}
